// ===========================================
// Security Audit API
// ===========================================
// API client for security audit log endpoints

import { apiClient } from '../services/api';

// ===========================================
// TYPES
// ===========================================

export interface SecurityAuditLog {
  id: string;
  schoolId: string;
  action: string;
  performedBy: string;
  targetUserId: string;
  details: Record<string, unknown>;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
  performedByUser: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
  targetUser: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
}

export interface SecurityAuditFilters {
  action?: string;
  targetUserId?: string;
  performedBy?: string;
  startDate?: string;
  endDate?: string;
}

export interface SecurityAuditResponse {
  status: 'success';
  data: SecurityAuditLog[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

export interface AuditAction {
  value: string;
  label: string;
}

// ===========================================
// API FUNCTIONS
// ===========================================

/**
 * Get security audit logs with optional filtering
 */
export async function getSecurityAuditLogs(
  filters: SecurityAuditFilters = {},
  limit = 100,
  offset = 0
): Promise<SecurityAuditResponse> {
  const params = new URLSearchParams();

  if (filters.action) params.set('action', filters.action);
  if (filters.targetUserId) params.set('targetUserId', filters.targetUserId);
  if (filters.performedBy) params.set('performedBy', filters.performedBy);
  if (filters.startDate) params.set('startDate', filters.startDate);
  if (filters.endDate) params.set('endDate', filters.endDate);
  params.set('limit', limit.toString());
  params.set('offset', offset.toString());

  return apiClient.get<SecurityAuditResponse>(
    `/security-audit?${params.toString()}`
  );
}

/**
 * Get available audit action types
 */
export async function getAuditActions(): Promise<AuditAction[]> {
  const response = await apiClient.get<{ status: 'success'; data: AuditAction[] }>(
    '/security-audit/actions'
  );
  return response.data;
}
